<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useLocalization } from '@hantera/portal-app'
import { LoadingArea } from '@hantera/design-system'
import useBackend from '@/composition/useBackend'
import type { JobDefinition } from '@/backend/jobs.types'
import ParamInput from './ParamInput.vue'
import {
  coerceDefault,
  defaultForKind,
  isEmptyValue,
  parseParamType,
  serializeValue,
  type ParamKind,
} from './paramTypes'

const props = defineProps<{
  jobDefinitionId: string
  defaultValues?: Record<string, unknown>
  onSchedule?: (parameters: Record<string, unknown>) => Promise<void>
  onCancel?: () => void
}>()

const t = useLocalization('jobs')
const { jobs: jobsService } = useBackend()

const isLoading = ref(true)
const loadError = ref<string | null>(null)
const isScheduling = ref(false)
const error = ref<string | null>(null)

const jobDefinition = ref<JobDefinition | null>(null)
const values = ref<Record<string, unknown>>({})

const parameterNames = computed(() => {
  if (!jobDefinition.value?.parameters) return []
  return Object.keys(jobDefinition.value.parameters)
})

const parameters = computed(() => jobDefinition.value?.parameters ?? {})
const hasParameters = computed(() => parameterNames.value.length > 0)

const kinds = computed<Record<string, ParamKind>>(() => {
  const result: Record<string, ParamKind> = {}
  for (const name of parameterNames.value) {
    result[name] = parseParamType(parameters.value[name]?.type)
  }
  return result
})

const canSchedule = computed(() => {
  if (isLoading.value || loadError.value) return false
  if (!hasParameters.value) return true
  return parameterNames.value.every((name) => {
    if (!parameters.value[name]?.isRequired) return true
    return !isEmptyValue(kinds.value[name], values.value[name])
  })
})

onMounted(loadDefinition)

async function loadDefinition() {
  isLoading.value = true
  loadError.value = null
  try {
    const definition = await jobsService.getJobDefinition(props.jobDefinitionId)
    jobDefinition.value = definition
    initializeValues(definition)
  } catch (e) {
    loadError.value = (e as Error).message ?? t('detail.scheduleDialog.loadFailed')
  } finally {
    isLoading.value = false
  }
}

function initializeValues(definition: JobDefinition) {
  const initial: Record<string, unknown> = {}
  const params = definition.parameters ?? {}
  for (const name of Object.keys(params)) {
    const kind = parseParamType(params[name]?.type)
    const raw = props.defaultValues?.[name]
    initial[name] =
      raw === undefined || raw === null
        ? defaultForKind(kind)
        : coerceDefault(kind, raw)
  }
  values.value = initial
}

function buildParameters(): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const name of parameterNames.value) {
    const kind = kinds.value[name]
    const value = values.value[name]
    if (isEmptyValue(kind, value)) continue
    try {
      result[name] = serializeValue(kind, value)
    } catch (e) {
      throw new Error(`${name}: ${(e as Error).message}`)
    }
  }
  return result
}

async function handleSchedule() {
  if (!props.onSchedule) return

  error.value = null
  isScheduling.value = true

  try {
    const params = hasParameters.value ? buildParameters() : {}
    await props.onSchedule(params)
  } catch (e) {
    error.value = (e as Error).message
  } finally {
    isScheduling.value = false
  }
}

function handleCancel() {
  props.onCancel?.()
}
</script>

<template>
  <div class="schedule-dialog-content p2">
    <LoadingArea :loading="isLoading">
      <template v-if="loadError">
        <div class="error">{{ loadError }}</div>
      </template>

      <template v-else-if="!isLoading">
        <template v-if="hasParameters">
          <div class="box-content-label">
            {{ t('detail.scheduleDialog.parameters') }}
          </div>
          <div class="box-content p">
            <div class="param-form">
              <div
                v-for="name in parameterNames"
                :key="name"
                class="param-row"
              >
                <label class="param-label" :for="`param-${name}`">
                  <span class="param-name monospace">{{ name }}</span>
                  <span class="param-type monospace">{{ parameters[name]?.type }}</span>
                  <span
                    v-if="!parameters[name]?.isRequired"
                    class="optional-badge"
                  >
                    {{ t('detail.scheduleDialog.optional') }}
                  </span>
                </label>
                <ParamInput
                  :kind="kinds[name]"
                  :model-value="values[name]"
                  :disabled="isScheduling"
                  :input-id="`param-${name}`"
                  @update:model-value="(v) => (values[name] = v)"
                />
                <div
                  v-if="kinds[name].kind === 'json'"
                  class="field-help"
                >
                  {{ t('detail.scheduleDialog.jsonHint') }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="box-content p">
            <p class="no-params-message">
              {{ t('detail.scheduleDialog.noParameters') }}
            </p>
          </div>
        </template>
      </template>
    </LoadingArea>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="dialog-actions">
      <button
        class="btn-secondary"
        :disabled="isScheduling"
        @click="handleCancel"
      >
        {{ t('detail.scheduleDialog.cancel') }}
      </button>
      <button
        class="btn-primary"
        :disabled="isScheduling || !canSchedule"
        @click="handleSchedule"
      >
        {{ isScheduling
          ? t('detail.scheduleDialog.scheduling')
          : t('detail.scheduleDialog.scheduleNow') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.schedule-dialog-content {
  display: flex;
  flex-direction: column;
  min-width: 400px;
  max-width: 600px;
}

.monospace {
  font-family: monospace;
}

.param-form {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.param-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.param-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
}

.param-name {
  font-weight: 600;
  color: hsl(var(--color-20));
}

.param-type {
  font-size: 11px;
  color: hsl(var(--color-50));
}

.optional-badge {
  font-size: 10px;
  font-weight: 500;
  color: hsl(var(--color-50));
  font-style: italic;
}

.field-help {
  font-size: 11px;
  color: hsl(var(--color-50));
}

.no-params-message {
  margin: 0;
  font-size: 13px;
  color: hsl(var(--color-40));
}

.error {
  background: hsl(0, 84%, 95%);
  color: hsl(0, 84%, 40%);
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 13px;
}

.dialog-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid hsl(var(--color-90));
}
</style>
